/*
 * Copyright (c) 2026 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, May 29th 2026, 8:50:54 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import { useOSFormContext } from "ui-widgets/forms/providers";
import ListItemText from "@mui/material/ListItemText";
import { Activity as ActivityIcon } from "react-feather";
import BPMNSignalIcon from "@iconify/icons-bpmn/intermediate-event-catch-signal";
import { BPMNIcon } from "./TaskTemplateIcons";
import { useServiceBuilderContext } from "service-builder/providers";
import { TimerEventConfigurationInput } from "common/components/time";
import { useGlobalScope } from "core/providers";
import { APISERVICEPATHS } from "lib/api";
import { getDefaultEventCode, getDefaultBoundaryEventConfiguration, getDefaultEventConfiguration } from "lib/factories";
import {
  BoundaryEventConfiguration,
  EventCode,
  EventConfiguration,
  EventDirection,
  EventType,
  CatchEventType,
  ThrowEventType,
  TaskTemplate,
} from "lib/schemas";
import { useBoundaryEventHandlers } from "service-builder/hooks";
import { TaskTemplateInput } from "service-builder/components/workflow-builder/TaskTemplateInput";
import { BPMNEventIcon } from "service-builder/components/workflow-builder/TaskTemplateIcons";
import {
  useBoundaryEventConfigurationAPIForm,
  useEventCodeAPIForm,
  useEventConfigurationAPIForm,
  useDeleteBoundaryEvent,
  // useDeleteEventCode,
  // useDeleteEventConfiguration,
} from "service-builder/services";
import { COLORSCHEME } from "theme/styles";
import { ConcreteAPIMany2ManyDropDownFormInputProps, InputHelpDialogWrapper, StyledOSDialog } from "ui-widgets";
import {
  APIOne2ManyListEditorFormInput,
  APIOne2ManyListEditorItem,
  APIMany2ManyDropDownFormInput,
  OSAPIForm,
  OSAPIFormProps,
  TextFormInput,
  SwitchFormInput,
  FormSubmitBubblingStopper,
  DropDownFormInput,
} from "ui-widgets/forms";
import {
  APIMany2OneEditButton,
  APIMany2OneSelectFormInput,
  ConcreteAPIMany2OneSelectFormInputProps,
  EmphasisText,
  BodyTypography,
  VerticalStackWidget,
  FormOptionsSeparator,
  useToggleState,
  HelperText,
  OSAlert,
  OSActionButton,
} from "ui-widgets";

export const BoundaryEventsSettings = () => {
  const { watch: watchHost } = useOSFormContext<TaskTemplate>();
  const hostNode = watchHost();
  const helpText =
    "Provide configuration details for the boundary events for this node. This node will listen to these events and when any one of the events is dispatched / thrown, the target node will be activated. If this boundary event is set to an interrupting event, then this node will be cancelled";

  return (
    <Box sx={{ width: "100%", padding: 4 }}>
      <Stack spacing={2}>
        <InputHelpDialogWrapper
          helpTitle="Boundary Events (Listen to these events on this node)"
          helpContent={
            <VerticalStackWidget>
              <BodyTypography>{helpText}.</BodyTypography>

              <FormOptionsSeparator
                seperatorText={<EmphasisText>IMPORTANT NOTE</EmphasisText>}
                seperatorTextStyle={{ width: 100 }}
              />

              <BodyTypography>
                <EmphasisText>
                  This node must be active in order to listen to this event. A node that has not yet been spawned /
                  activated cannot be able to listen to events as it does not yet exist.
                </EmphasisText>
              </BodyTypography>
            </VerticalStackWidget>
          }
        >
          <HelperText>{helpText}.</HelperText>
        </InputHelpDialogWrapper>

        <BoundaryEventsListEditor hostNode={hostNode} />
      </Stack>
    </Box>
  );
};

export const BoundaryEventsListEditor = ({ hostNode }: { hostNode: TaskTemplate }) => {
  const {
    watchedTaskTemplateId,
    getBoundaryEvent,
    onDeleteBoundaryEvent,
    onSaveBoundaryEvent: onSaveBoundaryEventCallBack,
    getTargetNode,
  } = useBoundaryEventListLogic();

  const { handleClose, handleOpen, open: popUpOpen } = useToggleState();
  const { watch: watchHost } = useOSFormContext<TaskTemplate>();
  return (
    <>
      <APIOne2ManyListEditorFormInput<TaskTemplate, BoundaryEventConfiguration>
        apiServicePath={APISERVICEPATHS.boundaryEventConfigurations}
        apiQueryParams={{
          host_node_id: watchHost("id"),
        }}
        foreignKey="host_node"
        foreignKeyId={watchedTaskTemplateId}
        uniqueIdentifier="id"
        renderListView={({
          itemsListState: { savedItems: boundaryEvents, selectedItemId: selectedBoundaryEventId, itemCreationQueue },
          updateListAndSelectIfNewItem: updateBoundaryEventList,
          createNewItem: createNewBoundaryEvent,
          selectItem: selectBoundaryEvent,
          findItem: findBoundaryEvent,
        }) => {
          const onClickAddBoundaryEvent = () => {
            createNewBoundaryEvent(getBoundaryEvent());
            selectBoundaryEvent(null);
            handleOpen();
          };

          const onClickEditBoundaryEvent = (boundaryEventId: BoundaryEventConfiguration["id"]) => {
            selectBoundaryEvent(boundaryEventId);
            handleOpen();
          };

          const getBoundaryEventToEdit = (): BoundaryEventConfiguration => {
            return selectedBoundaryEventId ? findBoundaryEvent(selectedBoundaryEventId).item : itemCreationQueue[0];
          };

          const onSaveBoundaryEvent = (boundaryEvent: BoundaryEventConfiguration) => {
            updateBoundaryEventList(boundaryEvent);
            onSaveBoundaryEventCallBack(boundaryEvent);
          };

          return (
            <>
              <List sx={{ width: "100%", bgcolor: "background.paper" }}>
                <Stack spacing={2}>
                  {boundaryEvents.map((boundaryEvent) => {
                    const targetNode = getTargetNode(boundaryEvent);
                    return (
                      <APIOne2ManyListEditorItem
                        key={boundaryEvent.id}
                        listItemId={boundaryEvent.id}
                        useDeleteOSItem={useDeleteBoundaryEvent}
                        primaryText={boundaryEvent.title}
                        secondaryText={
                          <div>
                            <Typography
                              sx={{ display: "inline" }}
                              component="span"
                              variant="subtitle2"
                              color="text.primary"
                            >
                              Activates{" "}
                            </Typography>
                            <EmphasisText>{targetNode.title} </EmphasisText>
                            on catching the <EmphasisText>{boundaryEvent.event_code_label}</EmphasisText> event.
                            {boundaryEvent.is_interrupting ? ` and cancels ${hostNode.title}` : ""}
                          </div>
                        }
                        onClickEditItem={() => onClickEditBoundaryEvent(boundaryEvent.id)}
                        onDeleteItemCallback={() => onDeleteBoundaryEvent(boundaryEvent.id, hostNode.id)}
                        listItemIcon={
                          <BPMNEventIcon
                            eventDirection="catch"
                            eventType={boundaryEvent.event_type}
                            isInterrupting={boundaryEvent.is_interrupting}
                            width={30}
                            cursor="pointer"
                            color={COLORSCHEME.secondary}
                          />
                        }
                      />
                    );
                  })}
                </Stack>
                <BoundaryEventConfigurationModal
                  hostNodeId={hostNode.id}
                  boundaryEventConfiguration={getBoundaryEventToEdit()}
                  onUpdateBoundaryEventConfiguration={onSaveBoundaryEvent}
                  handleClose={handleClose}
                  open={popUpOpen}
                />
              </List>
              <OSActionButton actionStyle="add" onClick={onClickAddBoundaryEvent}>
                Add Boundary Event
              </OSActionButton>
            </>
          );
        }}
      />
    </>
  );
};

const useBoundaryEventListLogic = () => {
  const { watch } = useOSFormContext<TaskTemplate>();
  const { onSaveBoundaryEvent, onDeleteBoundaryEvent, getTargetNode } = useBoundaryEventHandlers();

  const [watchedTaskTemplateId, watchedTaskTemplateOrgId] = watch<(keyof TaskTemplate)[]>(["id", "artifact_org"]);

  const getBoundaryEvent = (): BoundaryEventConfiguration => {
    return {
      ...getDefaultBoundaryEventConfiguration(watchedTaskTemplateOrgId as string),
      host_node: watchedTaskTemplateId as TaskTemplate["id"],
    };
  };

  return { watchedTaskTemplateId, getBoundaryEvent, onSaveBoundaryEvent, onDeleteBoundaryEvent, getTargetNode };
};

export const BoundaryEventsSettingsAsDropDown = () => {
  return (
    <InputHelpDialogWrapper
      helpTitle="Boundary Events (Listen to these events on this node)"
      helpContent={
        <VerticalStackWidget>
          <BodyTypography>
            Provide configuration details for the boundary events for this node. This node will listen to these events
            and when any one of the events is dispatched / thrown, the target node will be activated. If this boundary
            event is set to an interrupting event, then this node will be cancelled.
          </BodyTypography>

          <FormOptionsSeparator
            seperatorText={<EmphasisText>IMPORTANT NOTE</EmphasisText>}
            seperatorTextStyle={{ width: 100 }}
          />

          <BodyTypography>
            <EmphasisText>
              This node must be active in order to listen to this event. A node that has not yet been spawned /
              activated cannot be able to listen to events as it does not yet exist.
            </EmphasisText>
          </BodyTypography>
        </VerticalStackWidget>
      }
    >
      <BoundaryEventConfigurationMany2ManyPicker<TaskTemplate>
        schema_field_key="boundary_events"
        label="Boundary Events (Listen to these events on this node)"
      />
    </InputHelpDialogWrapper>
  );
};

export const EventNodeTypeSettings = () => {
  const { watch, setValue } = useOSFormContext<TaskTemplate>();
  const eventDirection = watch("event_direction");
  const eventConfigurationId = watch("event_configuration");
  const label =
    eventDirection === "throw"
      ? "Throw Event (Dispatch this event from this node)"
      : "Catch Event (Listen to this event from this node)";
  const helpText =
    eventDirection === "throw"
      ? "Select or create a throw event for this node. When this node is executed and marked as DONE, it will dispatch the selected event"
      : "Select or create a catch event for this node. When the catch event is dispatched, this node will complete, be marked as DONE and the next node activated";

  return (
    <>
      <EventConfigurationMany2OnePicker<TaskTemplate>
        schema_field_key="event_configuration"
        label={label}
        eventConfigurationId={eventConfigurationId}
        setValue={setValue}
        eventDirection={eventDirection}
      />
      <HelperText>{helpText}</HelperText>
    </>
  );
};

export const BoundaryEventConfigurationMany2ManyPicker = <T,>(
  props: ConcreteAPIMany2ManyDropDownFormInputProps<T, BoundaryEventConfiguration>
) => {
  const getConfigLabel = (config: BoundaryEventConfiguration) => `${config.title} | ${config?.event_code_label}`;

  const { watch: watchHost, setValue: setHostValue } = useOSFormContext<TaskTemplate>();
  const { deleteItem } = useDeleteBoundaryEvent();
  const onDeleteSuccess = (deletedId: TaskTemplate["id"]) => {
    const current = watchHost("boundary_events") as BoundaryEventConfiguration["id"][];
    setHostValue(
      "boundary_events",
      current.filter((id) => deletedId !== id)
    );
  };
  const hostNodeId = watchHost("id");

  return (
    <>
      <APIMany2ManyDropDownFormInput<T, BoundaryEventConfiguration>
        apiServicePath={APISERVICEPATHS.boundaryEventConfigurations}
        apiQueryParams={{
          host_node_id: hostNodeId,
        }}
        itemIdProperty="id"
        isFetchingAPIItem={false}
        getOptionLabel={getConfigLabel}
        label="Boundary Events"
        getOptionComponent={(option) => (
          <ListItemText primary={`${option.title}`} secondary={`${option.event_code_label}`} />
        )}
        {...props}
        onDeleteItem={(id) => {
          deleteItem(id, { onDelete: onDeleteSuccess });
        }}
        InputProps={{
          startAdornment: <BPMNIcon icon={BPMNSignalIcon} color={COLORSCHEME.primary} fontSize="large" />,
        }}
        renderUpdateItemPopUp={({ item, onUpdateItem, closePopup, popUpOpen }) => {
          return (
            <BoundaryEventConfigurationModal
              hostNodeId={hostNodeId}
              boundaryEventConfiguration={item}
              onUpdateBoundaryEventConfiguration={onUpdateItem}
              handleClose={closePopup}
              open={popUpOpen}
            />
          );
        }}
      />
    </>
  );
};

type BoundaryEventConfigurationModalProps = Parameters<typeof useBoundaryEventConfigurationAPIForm>[0] & {
  handleClose: () => void;
  open: boolean;
  hostNodeId: TaskTemplate["id"];
};

export const BoundaryEventConfigurationModal = ({
  boundaryEventConfiguration,
  onUpdateBoundaryEventConfiguration,
  handleClose,
  open,
  hostNodeId,
}: BoundaryEventConfigurationModalProps) => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const defaultBoundaryEventConfiguration =
    boundaryEventConfiguration ?? getDefaultBoundaryEventConfiguration(selectedOrganization.id);

  const useAPIFormReturn = useBoundaryEventConfigurationAPIForm({
    boundaryEventConfiguration: defaultBoundaryEventConfiguration,
    onUpdateBoundaryEventConfiguration,
  });

  return (
    <>
      <StyledOSDialog
        popUpIcon={<ActivityIcon color={COLORSCHEME.primary} />}
        popUpTitle="Boundary Event Configuration"
        open={open}
        onClose={handleClose}
        popUpDescription="Provide configuration details for the boundary event."
      >
        <BoundaryEventConfigurationModalForm useAPIFormReturn={useAPIFormReturn} hostNodeId={hostNodeId} />
      </StyledOSDialog>
    </>
  );
};

const BoundaryEventConfigurationModalForm = ({
  useAPIFormReturn,
  hostNodeId,
}: Pick<OSAPIFormProps<BoundaryEventConfiguration>, "useAPIFormReturn"> & { hostNodeId: TaskTemplate["id"] }) => {
  const {
    useFormReturn: { setValue, watch: watchBoundaryEvent },
  } = useAPIFormReturn;
  const {
    processDefinitionContext: { currentProcessDefinition },
  } = useServiceBuilderContext();

  React.useEffect(() => {
    setValue("host_node", hostNodeId);
  }, [setValue, hostNodeId]);

  const eventConfigurationId = watchBoundaryEvent("event_configuration");

  return (
    <OSAPIForm useAPIFormReturn={useAPIFormReturn} loaderType="form-skeleton">
      <TextFormInput<BoundaryEventConfiguration> schema_field_key="title" label="Title" />
      <TaskTemplateInput<BoundaryEventConfiguration>
        schema_field_key="target_node"
        label="Target Node"
        apiQueryParams={{
          process_definition: currentProcessDefinition.id,
        }}
      />
      <EventConfigurationMany2OnePicker<BoundaryEventConfiguration>
        schema_field_key="event_configuration"
        label="Event Configuration"
        setValue={setValue}
        eventConfigurationId={eventConfigurationId}
        eventDirection="catch"
      />
      <SwitchFormInput<BoundaryEventConfiguration> schema_field_key="enabled" label="Enabled" />
      <InputHelpDialogWrapper
        helpTitle="Interrupting vs Non-Interrupting Boundary Events"
        helpContent={
          <>
            An interrupting boundary event will result in a cancellation of the host node whereas a non-interrupting
            boundary event will leave the host node in the status it was found.
          </>
        }
      >
        <SwitchFormInput<BoundaryEventConfiguration>
          schema_field_key="is_interrupting"
          label="Interrupting Boundary Event?"
        />
      </InputHelpDialogWrapper>
    </OSAPIForm>
  );
};

interface EventConfigurationOnPickerProps<PrimaryObject>
  extends ConcreteAPIMany2OneSelectFormInputProps<PrimaryObject, EventConfiguration> {
  getOptionLabel?: (option: EventConfiguration) => string;
  eventConfigurationId: EventConfiguration["id"];
  setValue: (fieldName: string, value: EventConfiguration["id"]) => void;
  eventDirection: EventConfiguration["event_direction"];
}

export const EventConfigurationMany2OnePicker = <PrimaryObject,>(
  props: EventConfigurationOnPickerProps<PrimaryObject>
) => {
  const { handleOpen: handleOpenEdit, handleClose: handleCloseEdit, open: editIsOpen } = useToggleState();

  return (
    <>
      <APIMany2OneSelectFormInput<PrimaryObject, EventConfiguration>
        label={props.label ?? "Event Configurations"}
        itemIdProperty="id"
        loadingText="Loading Event Configurations"
        getOptionLabel={(option) => `${option.event_code_label} | ${option.title} (${option.event_type})`}
        apiServicePath={APISERVICEPATHS.eventConfigurations}
        apiQueryParams={{ event_direction: props.eventDirection }}
        inputStartAdornment={<BPMNIcon icon={BPMNSignalIcon} color={COLORSCHEME.primary} fontSize="large" />}
        inputEndAdornment={<APIMany2OneEditButton objectId={props.eventConfigurationId} onClick={handleOpenEdit} />}
        getOptionComponent={(option) => (
          <ListItemText
            primary={`${option.title} | ${EventDirection[option.event_direction]}`}
            secondary={`${option.event_code_label} (${option.event_type})`}
          />
        )}
        renderRelatedObjectForm={({ handleCloseForm, formOpenState }) => (
          <EventConfigurationModal
            onUpdateEventConfiguration={(eventConfiguration) =>
              props.setValue(props.schema_field_key, eventConfiguration.id)
            }
            handleClose={handleCloseForm}
            open={formOpenState}
            eventDirection={props.eventDirection}
          />
        )}
        {...props}
      />
      <EventConfigurationModal
        onUpdateEventConfiguration={(eventConfiguration) => {}}
        eventConfigurationId={props.eventConfigurationId}
        handleClose={handleCloseEdit}
        open={editIsOpen}
        eventDirection={props.eventDirection}
      />
    </>
  );
};

type EventConfigurationModalProps = Parameters<typeof useEventConfigurationAPIForm>[0] & {
  handleClose: () => void;
  open: boolean;
  eventDirection: EventConfiguration["event_direction"];
};

export const EventConfigurationModal = ({
  eventConfiguration,
  eventConfigurationId,
  eventDirection,
  onUpdateEventConfiguration,
  handleClose,
  open,
}: EventConfigurationModalProps) => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const defaultEventConfiguration =
    eventConfiguration || getDefaultEventConfiguration(selectedOrganization.id, eventDirection);
  const useAPIFormReturn = useEventConfigurationAPIForm({
    eventConfiguration: defaultEventConfiguration,
    onUpdateEventConfiguration,
    eventConfigurationId,
  });

  return (
    <>
      <StyledOSDialog
        popUpIcon={<ActivityIcon color={COLORSCHEME.primary} />}
        popUpTitle="Event Configuration"
        open={open}
        onClose={handleClose}
        popUpDescription="Provide configuration details for the event."
      >
        <FormSubmitBubblingStopper>
          <EventConfigurationForm useAPIFormReturn={useAPIFormReturn} />
        </FormSubmitBubblingStopper>
      </StyledOSDialog>
    </>
  );
};

export const EventConfigurationForm = ({
  useAPIFormReturn,
  showSubmitButton,
}: Pick<OSAPIFormProps<EventConfiguration>, "showSubmitButton" | "useAPIFormReturn">) => {
  const {
    useFormReturn: { setValue, watch: watchEventConfiguration },
    apiDataSendErrors,
  } = useAPIFormReturn;
  const eventCodeId = watchEventConfiguration("event_code");
  const configuration = watchEventConfiguration("configuration");
  const isTimerEvent = (eventType: keyof typeof EventType) => eventType === "Timer";
  const hasErrors = Boolean(apiDataSendErrors?.non_field_errors?.length);
  const watchedEventDirection = watchEventConfiguration("event_direction");
  const setDefaultEventConfiguration = (eventType: keyof typeof EventType) => {
    if (isTimerEvent(eventType)) {
      if (!!configuration?.timer_expression) return;

      return setValue("configuration", {
        timer_expression_type: "iso_8601",
        timer_expression: "PT1H",
        timer_countdown_from: "task_created",
      });
    }
    setValue("configuration", {});
  };

  return (
    <OSAPIForm useAPIFormReturn={useAPIFormReturn} loaderType="form-skeleton" showSubmitButton={showSubmitButton}>
      {hasErrors ? (
        <OSAlert severity="error" alertTitle="Kindly attend to the errors below:">
          <ul>
            {apiDataSendErrors?.non_field_errors?.map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </OSAlert>
      ) : null}
      <TextFormInput<EventConfiguration> schema_field_key="title" label="Title" />
      {watchedEventDirection ? (
        <EventCodeMany2OnePicker<EventConfiguration>
          schema_field_key="event_code"
          label="Event Code"
          setValue={setValue}
          eventCodeId={eventCodeId}
          onChangeCallBack={(eventCode) => {
            setValue("event_type", eventCode.event_type);
            setDefaultEventConfiguration(eventCode.event_type);
          }}
          eventDirection={watchedEventDirection}
        />
      ) : null}
      {isTimerEvent(watchEventConfiguration("event_type")) && (
        <TimerEventConfigurationInput
          configuration={configuration}
          setValue={(field_name, value) => setValue(`configuration.${field_name}` as any, value as any)}
        />
      )}
    </OSAPIForm>
  );
};

interface EventCodeOnPickerProps<PrimaryObject>
  extends ConcreteAPIMany2OneSelectFormInputProps<PrimaryObject, EventCode> {
  getOptionLabel?: (option: EventCode) => string;
  eventCodeId: EventCode["id"];
  setValue: (fieldName: string, value: EventCode["id"]) => void;
  eventDirection: keyof typeof EventDirection;
}

export const EventCodeMany2OnePicker = <PrimaryObject,>(props: EventCodeOnPickerProps<PrimaryObject>) => {
  const onChangeEventCode = (eventCode: EventCode) => {
    props.setValue(props.schema_field_key, eventCode.id);
    props.onChangeCallBack(eventCode);
  };
  return (
    <>
      <APIMany2OneSelectFormInput<PrimaryObject, EventCode>
        label={props.label ?? "Event Codes"}
        itemIdProperty="id"
        loadingText="Loading Event Codes"
        getOptionLabel={(option) => `${option.code} | ${option.name} (${option.event_type})`}
        apiServicePath={APISERVICEPATHS.eventCodes}
        apiQueryParams={props.eventDirection ? { event_direction: props.eventDirection } : null}
        inputStartAdornment={<BPMNIcon icon={BPMNSignalIcon} color={COLORSCHEME.primary} fontSize="large" />}
        getOptionComponent={(option) => (
          <ListItemText primary={option.name} secondary={`${option.code} (${option.event_type})`} />
        )}
        renderRelatedObjectForm={({ handleCloseForm, formOpenState, isNew }) => (
          <EventCodeModal
            onUpdateEventCode={onChangeEventCode}
            handleClose={handleCloseForm}
            open={formOpenState}
            eventDirection={props.eventDirection}
            eventCodeId={isNew ? null : props.eventCodeId}
          />
        )}
        {...props}
      />
    </>
  );
};

type EventCodeModalProps = Parameters<typeof useEventCodeAPIForm>[0] & {
  handleClose: () => void;
  open: boolean;
  eventDirection: keyof typeof EventDirection;
};

export const EventCodeModal = ({
  eventCode,
  eventCodeId,
  onUpdateEventCode,
  handleClose,
  open,
  eventDirection,
}: EventCodeModalProps) => {
  return (
    <>
      <StyledOSDialog
        popUpIcon={<ActivityIcon color={COLORSCHEME.primary} />}
        popUpTitle="Event Code"
        open={open}
        onClose={handleClose}
        popUpDescription="Provide configuration details for the event code."
      >
        <FormSubmitBubblingStopper>
          <EventCodeForm
            eventCode={eventCode}
            onUpdateEventCode={onUpdateEventCode}
            eventCodeId={eventCodeId}
            eventDirection={eventDirection}
          />
        </FormSubmitBubblingStopper>
      </StyledOSDialog>
    </>
  );
};

const EventCodeForm = ({
  eventCode,
  eventCodeId,
  onUpdateEventCode,
  eventDirection,
}: Parameters<typeof useEventCodeAPIForm>[0]) => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const defaultEventCode = eventCode || getDefaultEventCode(selectedOrganization.id);

  const useAPIFormReturn = useEventCodeAPIForm({
    eventCode: defaultEventCode,
    onUpdateEventCode,
    eventCodeId,
    eventDirection,
  });

  let eventTypeOptions: typeof EventType | {};

  switch (eventDirection) {
    case "catch":
      eventTypeOptions = CatchEventType;
      break;
    case "throw":
      eventTypeOptions = ThrowEventType;
      break;
    default:
      eventTypeOptions = {};
  }

  return (
    <OSAPIForm useAPIFormReturn={useAPIFormReturn} loaderType="form-skeleton">
      <TextFormInput<EventCode> schema_field_key="name" label="Name" />
      <TextFormInput<EventCode> schema_field_key="code" label="Code" />
      <DropDownFormInput<EventCode> schema_field_key="event_type" label="Event Type" options={eventTypeOptions} />
    </OSAPIForm>
  );
};

// export const EventCodeMany2ManyPicker = <T,>(props: ConcreteAPIMany2ManyDropDownFormInputProps<T, EventCode>) => {
//   const { watch: watchHost, setValue: setHostValue } = useOSFormContext<TaskTemplate>();
//   const { deleteItem } = useDeleteEventCode();
//   const onDeleteSuccess = (deletedId: TaskTemplate["id"]) => {
//     setHostValue("event_code", null);
//   };

//   return (
//     <>
//       <APIMany2ManyDropDownFormInput<T, EventCode>
//         apiServicePath={APISERVICEPATHS.eventCodes}
//         apiQueryParams={{
//           host_node_id: watchHost("id"),
//         }}
//         itemIdProperty="id"
//         isFetchingAPIItem={false}
//         onDeleteItem={(id) => {
//           deleteItem(id, { onDelete: onDeleteSuccess });
//         }}
//         getOptionLabel={(option) => `${option?.name}`}
//         label="Events"
//         getOptionComponent={(option) => (
//           <ListItemText primary={option.name} secondary={`[${option?.code}] ${option?.event_type}`} />
//         )}
//         {...props}
//         InputProps={{
//           startAdornment: <BPMNIcon icon={BPMNSignalIcon} color={COLORSCHEME.primary} fontSize="large" />,
//         }}
//         renderUpdateItemPopUp={({ item, onUpdateItem, closePopup, popUpOpen }) => {
//           return (
//             <EventCodeModal
//               eventCode={item}
//               onUpdateEventCode={onUpdateItem}
//               handleClose={closePopup}
//               open={popUpOpen}
//             />
//           );
//         }}
//       />
//     </>
//   );
// };
